import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { MessageService } from 'primeng/api'

import { ApiCustomerVerificationService, IResponseWithCode } from '../../data/services/api/api-customer-verification.service'
import { CiecRequestAttempt, CiecRequestListItem, ClientCiecStatus } from '@data/interfaces/ciec-request.interface'
import { CiecFailureInfo, CiecLabel, CiecTranslateService } from '@shared/services/ciec-translate.service'

/**
 * Aviso de la solicitud más reciente. El texto vive en la plantilla (lleva fechas con pipe); aquí solo
 * se decide cuál toca.
 */
export type CiecRequestBannerKind = 'pending' | 'rejected' | 'exhausted' | 'provider' | 'rfc' | 'pending-sat' | 'no-credential' | 'legacy-failed'

export interface CiecRequestBanner {
  kind: CiecRequestBannerKind
  tone: 'info' | 'warn' | 'danger'
  request: CiecRequestListItem
}

/**
 * Modal de solicitud de CIEC al cliente (ADR-0032, B-075).
 *
 * Flujo:
 *   1. step='confirm' — aviso de la solicitud más reciente, historial con los intentos del cliente y
 *      «Generar y enviar» (o «Enviar enlace nuevo» si hay una viva o agotada).
 *   2. step='sent' — banner de éxito, enlace copiable, «Reenviar correo» y «Cerrar».
 *
 * Reglas:
 *   - El backend cancela el `pending` previo al crear uno nuevo (un solo enlace vivo), así que el
 *     modal avisa ANTES de enviar que el anterior deja de servir y si acumuló rechazos del SAT: al
 *     regenerar el contador vuelve a cero (decisión de Arnold, 8-oct-2026: el tope es por enlace).
 *   - Reenviar regenera (cancela + nuevo): el token plano nunca se persiste.
 *   - El historial se traduce con `CiecTranslateService`: el `outcome` lo calcula el servidor.
 *   - `(changed)` avisa al padre de cualquier cambio (generar, cancelar, reenviar) para que refresque
 *     lo que pinta de la CIEC; el toast lo pone este modal, no el padre.
 */
@Component({
  selector: 'app-ciec-request-dialog',
  templateUrl: './ciec-request-dialog.component.html',
  styleUrls: ['./ciec-request-dialog.component.scss'],
})
export class CiecRequestDialogComponent implements OnChanges {
  @Input() visible = false
  @Input() clientId = ''
  @Input() clientEmail = ''
  @Input() clientName = ''
  //* B-075 · Con CIEC guardada, el modal avisa que un enlace nuevo solo hace falta si la cambió o el SAT la rechaza.
  @Input() ciecStatus: ClientCiecStatus | null = null

  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() changed = new EventEmitter<void>()
  //* El RFC se corrige en «Editar cliente», que vive en el padre.
  @Output() editClient = new EventEmitter<void>()

  step: 'confirm' | 'sent' = 'confirm'
  loading = false
  historyState: 'loading' | 'ready' | 'error' = 'loading'
  historyError = ''
  requests: CiecRequestListItem[] = []
  expandedIds = new Set<string>()
  busyRequestId: string | null = null
  //* Error con código al generar o reenviar: se queda en el modal, junto a la acción que lo resuelve.
  generateError: CiecFailureInfo | null = null

  generatedLink = ''
  expiresAt: Date | null = null
  sentToEmail = ''
  sentAt: Date | null = null

  constructor(
    private readonly api: ApiCustomerVerificationService,
    private readonly messageService: MessageService,
    private readonly translate: CiecTranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.resetState()
      this.loadHistory()
    }
  }

  private resetState(): void {
    this.step = 'confirm'
    this.loading = false
    this.generateError = null
    this.expandedIds = new Set<string>()
    this.generatedLink = ''
    this.expiresAt = null
    this.sentToEmail = ''
    this.sentAt = null
  }

  loadHistory(): void {
    if (!this.clientId) return
    this.historyState = 'loading'
    this.historyError = ''
    this.api.listCiecRequests(this.clientId).subscribe((r) => {
      if (r?.error) {
        this.requests = []
        this.historyState = 'error'
        this.historyError = r?.msg || 'No se pudo cargar el historial de solicitudes.'
        return
      }
      this.requests = Array.isArray(r?.data) ? (r.data as CiecRequestListItem[]) : []
      this.historyState = 'ready'
    })
  }

  close(): void {
    this.visible = false
    this.visibleChange.emit(false)
  }

  /** La más reciente: el servidor la manda primero. */
  get latest(): CiecRequestListItem | null {
    return this.requests[0] ?? null
  }

  /** La solicitud viva (solo puede haber una): es la que deja de servir al generar otra. */
  get activeRequest(): CiecRequestListItem | null {
    return this.requests.find((r) => r.status === 'pending') ?? null
  }

  /** Con una viva o una agotada, generar es «otro enlace», no el primero: la etiqueta lo dice. */
  get replacesPrevious(): boolean {
    return !!this.activeRequest || this.latest?.status === 'exhausted'
  }

  get generateLabel(): string {
    return this.replacesPrevious ? 'Enviar enlace nuevo' : 'Generar y enviar'
  }

  /** Rechazos del SAT que acumuló el último enlace: el nuevo arranca de cero y conviene saberlo. */
  get previousRejections(): number {
    return this.latest?.attemptsUsed ?? 0
  }

  get banner(): CiecRequestBanner | null {
    const request = this.latest
    if (!request) return null
    const code = request.lastAttempt?.code ?? null
    switch (request.outcome) {
      case 'pending':
      case 'pending_rejected':
        //* El último intento manda: un RFC mal o una caída del proveedor se resuelven distinto que un rechazo.
        if (code === 'RFC_MISSING' || code === 'RFC_INVALID') return { kind: 'rfc', tone: 'warn', request }
        if (this.translate.isProviderSideCode(code)) return { kind: 'provider', tone: 'danger', request }
        if (request.outcome === 'pending_rejected') return { kind: 'rejected', tone: 'warn', request }
        return { kind: 'pending', tone: 'info', request }
      case 'exhausted':
        return { kind: 'exhausted', tone: 'danger', request }
      case 'received_pending_sat':
        return { kind: 'pending-sat', tone: 'info', request }
      case 'legacy_no_credential':
        return { kind: 'no-credential', tone: 'danger', request }
      case 'legacy_rejected':
      case 'legacy_error':
      case 'legacy_failed':
        return { kind: 'legacy-failed', tone: 'danger', request }
      default:
        return null
    }
  }

  /** El motivo del SAT del último rechazo, si lo hay. */
  lastSatReason(request: CiecRequestListItem): string | null {
    const rejected = [...(request.attempts ?? [])].reverse().find((a) => a.code === 'CIEC_REJECTED')
    return rejected?.satReason || null
  }

  outcomeLabel(request: CiecRequestListItem): CiecLabel {
    return this.translate.describeRequestOutcome(request.outcome)
  }

  attemptLabel(attempt: CiecRequestAttempt | null): CiecLabel {
    return this.translate.describeAttempt(attempt)
  }

  /** Quién la pidió. `requestedBy` viene null en solicitudes de legado o si el usuario que la pidió ya no existe. */
  requesterName(request: CiecRequestListItem): string {
    const by = request.requestedBy
    const name = [by?.names, by?.lastNames].filter(Boolean).join(' ').trim()
    return name || '—'
  }

  /**
   * Las solicitudes anteriores a B-075 no tienen intentos: ahí «0/3» diría algo que no se sabe. El tope lo
   * manda el servidor; si no viene, «—» en vez de suponer uno.
   */
  attemptsLabel(request: CiecRequestListItem): string {
    if (!request.maxAttempts || (!request.attempts?.length && request.status !== 'pending')) return '—'
    return `${request.attemptsUsed ?? 0}/${request.maxAttempts}`
  }

  canResend(request: CiecRequestListItem): boolean {
    return request.status === 'pending' || request.status === 'expired'
  }

  isExpanded(request: CiecRequestListItem): boolean {
    return this.expandedIds.has(request._id)
  }

  toggleAttempts(request: CiecRequestListItem): void {
    if (this.expandedIds.has(request._id)) this.expandedIds.delete(request._id)
    else this.expandedIds.add(request._id)
  }

  generate(): void {
    if (this.loading || !this.clientId) return
    this.loading = true
    this.generateError = null
    this.api.createCiecRequest(this.clientId).subscribe((r) => this.handleLinkResponse(r, 'No se pudo generar la solicitud'))
  }

  resend(): void {
    //* «Reenviar correo» desde step='sent' equivale a regenerar: el backend no guarda el token plano.
    this.generate()
  }

  resendRequest(request: CiecRequestListItem): void {
    if (this.loading || !this.clientId || !this.canResend(request)) return
    this.loading = true
    this.busyRequestId = request._id
    this.generateError = null
    this.api.resendCiecRequest(this.clientId, request._id).subscribe((r) => this.handleLinkResponse(r, 'No se pudo reenviar la solicitud'))
  }

  private handleLinkResponse(r: IResponseWithCode, fallback: string): void {
    this.loading = false
    this.busyRequestId = null
    if (r?.error) {
      if (r.code) {
        this.generateError = this.translate.describeFailure(r.code, r.msg)
        return
      }
      this.messageService.add({ severity: 'error', summary: 'Error', detail: r?.msg || fallback })
      return
    }
    const data = r?.data
    this.generatedLink = data?.link || ''
    this.expiresAt = data?.expiresAt ? new Date(data.expiresAt) : null
    this.sentToEmail = data?.sentTo?.email || this.clientEmail
    this.sentAt = data?.sentTo?.sentAt ? new Date(data.sentTo.sentAt) : new Date()
    this.step = 'sent'
    this.messageService.add({ severity: 'success', summary: 'Correo enviado', detail: `Solicitud enviada a ${this.sentToEmail}` })
    this.changed.emit()
    this.loadHistory()
  }

  requestEditClient(): void {
    this.close()
    this.editClient.emit()
  }

  copyLink(): void {
    if (!this.generatedLink) return
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(this.generatedLink).then(
        () => this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Enlace copiado al portapapeles', life: 2500 }),
        () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo copiar el enlace' }),
      )
    }
  }

  cancelRequest(request: CiecRequestListItem): void {
    if (!request?._id || !this.clientId || this.busyRequestId) return
    this.busyRequestId = request._id
    this.api.cancelCiecRequest(this.clientId, request._id).subscribe((r) => {
      this.busyRequestId = null
      if (r?.error) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: r?.msg || 'No se pudo cancelar la solicitud' })
        return
      }
      this.messageService.add({ severity: 'info', summary: 'Cancelada', detail: 'Solicitud cancelada' })
      this.changed.emit()
      this.loadHistory()
    })
  }

  trackById(_idx: number, request: CiecRequestListItem): string {
    return request._id
  }
}
